/**
 * Runtime probe -- opt-in via ?memprobe=1 URL param.
 *
 * Tracks:
 *   - attached listeners + pending rAF handles (classic leak vectors)
 *   - live-command preview dispatch counts
 *   - coalesced viewport invalidate() flush counts by source
 *   - direct invalidate() call sites that bypass the coalescer
 *
 * The listener/rAF wrappers install once on module import when the URL param
 * is present. The explicit record*() counters are no-ops when the probe is
 * disabled, keeping production overhead negligible.
 */

import type { AppState } from "../app/storeTypes.ts";

let installed = false;
let listenerCount = 0;
let rafPending = 0;
const runtimeProbeEnabled = (() => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("memprobe") === "1";
  } catch {
    return false;
  }
})();

let commandApplyCalls = 0;
let commandApplyGizmoCalls = 0;
let commandApplyKeyboardCalls = 0;
let commandApplyEditCalls = 0;
let commandApplyObjectCalls = 0;
let commandApplyBoneCalls = 0;
let commandEnterCalls = 0;
let commandConfirmCalls = 0;
let commandCancelCalls = 0;
let commandConfirmLivePreviewCommitCalls = 0;
let commandConfirmActiveEditSessionCommitCalls = 0;

let liveEditPreviewPatchCalls = 0;
let liveEditPreviewClearCalls = 0;

let invalidateCalls = 0;
let invalidateStoreCalls = 0;
let invalidateCanvasPointerCalls = 0;
let invalidateFlyPointerCalls = 0;
let invalidateFlyKeyCalls = 0;
let invalidateCameraKeyCalls = 0;
let invalidateStoreSceneCalls = 0;
let invalidateStoreEditSelectionCalls = 0;
let invalidateStoreEditStateCalls = 0;
let invalidateStoreEditHoverCalls = 0;
let invalidateStoreCommandCalls = 0;
let invalidateStoreCameraCalls = 0;
let invalidateStoreCursorCalls = 0;
let invalidateStoreUiCalls = 0;
let invalidateCanvasPointerEditIdleCalls = 0;
let invalidateCanvasPointerEditCommandCalls = 0;
let invalidateCanvasPointerTransformCalls = 0;
let invalidateCanvasPointerNavigateCalls = 0;
let invalidateDirectCalls = 0;
let invalidateOrbitControlsChangeCalls = 0;
let invalidateCameraSyncCalls = 0;
let invalidateCameraMovementCalls = 0;
let invalidateViewLockCorrectionCalls = 0;
let invalidateCameraTransitionCalls = 0;

// Use a WeakMap + key so we can decrement correctly on removeEventListener
// even when callers don't pass the same options object reference.
const targetMap = new WeakMap<EventTarget, Map<string, number>>();

function listenerKey(type: string, capture: boolean): string {
  return `${type}|${capture ? "c" : "b"}`;
}

function normalizeCapture(
  opts: boolean | AddEventListenerOptions | EventListenerOptions | undefined,
): boolean {
  if (typeof opts === "boolean") return opts;
  return !!opts?.capture;
}

export interface ProbeCounts {
  installed: boolean;
  listeners: number;
  rafPending: number;
  commandApplyCalls: number;
  commandApplyGizmoCalls: number;
  commandApplyKeyboardCalls: number;
  commandApplyEditCalls: number;
  commandApplyObjectCalls: number;
  commandApplyBoneCalls: number;
  commandEnterCalls: number;
  commandConfirmCalls: number;
  commandCancelCalls: number;
  commandConfirmLivePreviewCommitCalls: number;
  commandConfirmActiveEditSessionCommitCalls: number;
  liveEditPreviewPatchCalls: number;
  liveEditPreviewClearCalls: number;
  invalidateCalls: number;
  invalidateStoreCalls: number;
  invalidateCanvasPointerCalls: number;
  invalidateFlyPointerCalls: number;
  invalidateFlyKeyCalls: number;
  invalidateCameraKeyCalls: number;
  invalidateStoreSceneCalls: number;
  invalidateStoreEditSelectionCalls: number;
  invalidateStoreEditStateCalls: number;
  invalidateStoreEditHoverCalls: number;
  invalidateStoreCommandCalls: number;
  invalidateStoreCameraCalls: number;
  invalidateStoreCursorCalls: number;
  invalidateStoreUiCalls: number;
  invalidateCanvasPointerEditIdleCalls: number;
  invalidateCanvasPointerEditCommandCalls: number;
  invalidateCanvasPointerTransformCalls: number;
  invalidateCanvasPointerNavigateCalls: number;
  invalidateDirectCalls: number;
  invalidateOrbitControlsChangeCalls: number;
  invalidateCameraSyncCalls: number;
  invalidateCameraMovementCalls: number;
  invalidateViewLockCorrectionCalls: number;
  invalidateCameraTransitionCalls: number;
}

export function getProbeCounts(): ProbeCounts {
  return {
    installed,
    listeners: listenerCount,
    rafPending,
    commandApplyCalls,
    commandApplyGizmoCalls,
    commandApplyKeyboardCalls,
    commandApplyEditCalls,
    commandApplyObjectCalls,
    commandApplyBoneCalls,
    commandEnterCalls,
    commandConfirmCalls,
    commandCancelCalls,
    commandConfirmLivePreviewCommitCalls,
    commandConfirmActiveEditSessionCommitCalls,
    liveEditPreviewPatchCalls,
    liveEditPreviewClearCalls,
    invalidateCalls,
    invalidateStoreCalls,
    invalidateCanvasPointerCalls,
    invalidateFlyPointerCalls,
    invalidateFlyKeyCalls,
    invalidateCameraKeyCalls,
    invalidateStoreSceneCalls,
    invalidateStoreEditSelectionCalls,
    invalidateStoreEditStateCalls,
    invalidateStoreEditHoverCalls,
    invalidateStoreCommandCalls,
    invalidateStoreCameraCalls,
    invalidateStoreCursorCalls,
    invalidateStoreUiCalls,
    invalidateCanvasPointerEditIdleCalls,
    invalidateCanvasPointerEditCommandCalls,
    invalidateCanvasPointerTransformCalls,
    invalidateCanvasPointerNavigateCalls,
    invalidateDirectCalls,
    invalidateOrbitControlsChangeCalls,
    invalidateCameraSyncCalls,
    invalidateCameraMovementCalls,
    invalidateViewLockCorrectionCalls,
    invalidateCameraTransitionCalls,
  };
}

export function resetRuntimeProbeActivity(): void {
  commandApplyCalls = 0;
  commandApplyGizmoCalls = 0;
  commandApplyKeyboardCalls = 0;
  commandApplyEditCalls = 0;
  commandApplyObjectCalls = 0;
  commandApplyBoneCalls = 0;
  commandEnterCalls = 0;
  commandConfirmCalls = 0;
  commandCancelCalls = 0;
  commandConfirmLivePreviewCommitCalls = 0;
  commandConfirmActiveEditSessionCommitCalls = 0;
  liveEditPreviewPatchCalls = 0;
  liveEditPreviewClearCalls = 0;
  invalidateCalls = 0;
  invalidateStoreCalls = 0;
  invalidateCanvasPointerCalls = 0;
  invalidateFlyPointerCalls = 0;
  invalidateFlyKeyCalls = 0;
  invalidateCameraKeyCalls = 0;
  invalidateStoreSceneCalls = 0;
  invalidateStoreEditSelectionCalls = 0;
  invalidateStoreEditStateCalls = 0;
  invalidateStoreEditHoverCalls = 0;
  invalidateStoreCommandCalls = 0;
  invalidateStoreCameraCalls = 0;
  invalidateStoreCursorCalls = 0;
  invalidateStoreUiCalls = 0;
  invalidateCanvasPointerEditIdleCalls = 0;
  invalidateCanvasPointerEditCommandCalls = 0;
  invalidateCanvasPointerTransformCalls = 0;
  invalidateCanvasPointerNavigateCalls = 0;
  invalidateDirectCalls = 0;
  invalidateOrbitControlsChangeCalls = 0;
  invalidateCameraSyncCalls = 0;
  invalidateCameraMovementCalls = 0;
  invalidateViewLockCorrectionCalls = 0;
  invalidateCameraTransitionCalls = 0;
}

export function recordCommandApply(
  state: Pick<AppState, "mode" | "editMode" | "commandBuffer">,
): void {
  if (!runtimeProbeEnabled) return;
  const cb = state.commandBuffer;
  if (!cb) return;
  commandApplyCalls++;

  if (cb?.gizmoInitiated) commandApplyGizmoCalls++;
  else commandApplyKeyboardCalls++;

  if (state.mode === "edit" && state.editMode) {
    commandApplyEditCalls++;
  } else if (cb?.type === "boneRotate" || cb?.type === "boneTranslate") {
    commandApplyBoneCalls++;
  } else {
    commandApplyObjectCalls++;
  }
}

export function recordCommandEnter(): void {
  if (!runtimeProbeEnabled) return;
  commandEnterCalls++;
}

export function recordCommandConfirm(source: "none" | "livePreview" | "activeEditSession"): void {
  if (!runtimeProbeEnabled) return;
  commandConfirmCalls++;
  if (source === "livePreview") commandConfirmLivePreviewCommitCalls++;
  else if (source === "activeEditSession")
    commandConfirmActiveEditSessionCommitCalls++;
}

export function recordCommandCancel(): void {
  if (!runtimeProbeEnabled) return;
  commandCancelCalls++;
}

export function recordLiveEditPreviewPatch(): void {
  if (!runtimeProbeEnabled) return;
  liveEditPreviewPatchCalls++;
}

export function recordLiveEditPreviewClear(): void {
  if (!runtimeProbeEnabled) return;
  liveEditPreviewClearCalls++;
}

export type InvalidateReason =
  | "store"
  | "storeScene"
  | "storeEditSelection"
  | "storeEditState"
  | "storeEditHover"
  | "storeCommand"
  | "storeCamera"
  | "storeCursor"
  | "storeUi"
  | "canvasPointerMove"
  | "canvasPointerMoveEditIdle"
  | "canvasPointerMoveEditCommand"
  | "canvasPointerMoveTransform"
  | "canvasPointerMoveNavigate"
  | "windowFlyPointerMove"
  | "windowFlyKeydown"
  | "cameraKey"
  | "orbitControlsChange"
  | "cameraSync"
  | "cameraMovementFrame"
  | "viewLockCorrection"
  | "cameraTransition"
  | "liveEditPreview";

export function recordInvalidate(reason: InvalidateReason | Iterable<InvalidateReason>): void {
  if (!runtimeProbeEnabled) return;
  invalidateCalls++;
  const reasons = typeof reason === "string" ? [reason] : reason;
  for (const item of reasons) {
    if (item === "store" || item.startsWith("store")) {
      invalidateStoreCalls++;
      if (item === "storeScene") invalidateStoreSceneCalls++;
      else if (item === "storeEditSelection") invalidateStoreEditSelectionCalls++;
      else if (item === "storeEditState") invalidateStoreEditStateCalls++;
      else if (item === "storeEditHover") invalidateStoreEditHoverCalls++;
      else if (item === "storeCommand") invalidateStoreCommandCalls++;
      else if (item === "storeCamera") invalidateStoreCameraCalls++;
      else if (item === "storeCursor") invalidateStoreCursorCalls++;
      else if (item === "storeUi") invalidateStoreUiCalls++;
    } else if (
      item === "canvasPointerMove" ||
      item.startsWith("canvasPointerMove")
    ) {
      invalidateCanvasPointerCalls++;
      if (item === "canvasPointerMoveEditIdle") {
        invalidateCanvasPointerEditIdleCalls++;
      } else if (item === "canvasPointerMoveEditCommand") {
        invalidateCanvasPointerEditCommandCalls++;
      } else if (item === "canvasPointerMoveTransform") {
        invalidateCanvasPointerTransformCalls++;
      } else if (item === "canvasPointerMoveNavigate") {
        invalidateCanvasPointerNavigateCalls++;
      }
    } else if (item === "windowFlyPointerMove") invalidateFlyPointerCalls++;
    else if (item === "windowFlyKeydown") invalidateFlyKeyCalls++;
    else if (item === "cameraKey") invalidateCameraKeyCalls++;
    else {
      invalidateDirectCalls++;
      if (item === "orbitControlsChange") invalidateOrbitControlsChangeCalls++;
      else if (item === "cameraSync") invalidateCameraSyncCalls++;
      else if (item === "cameraMovementFrame") invalidateCameraMovementCalls++;
      else if (item === "viewLockCorrection")
        invalidateViewLockCorrectionCalls++;
      else if (item === "cameraTransition")
        invalidateCameraTransitionCalls++;
    }
  }
}

export function installListenerProbe(): void {
  if (installed) return;
  if (typeof window === "undefined") return;

  const origAdd = EventTarget.prototype.addEventListener;
  const origRemove = EventTarget.prototype.removeEventListener;

  EventTarget.prototype.addEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    opts?: boolean | AddEventListenerOptions,
  ): void {
    origAdd.call(this, type, listener, opts);
    if (!listener) return;
    let m = targetMap.get(this);
    if (!m) {
      m = new Map();
      targetMap.set(this, m);
    }
    const key = listenerKey(type, normalizeCapture(opts));
    const prev = m.get(key) ?? 0;
    m.set(key, prev + 1);
    listenerCount++;
  };

  EventTarget.prototype.removeEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    opts?: boolean | EventListenerOptions,
  ): void {
    origRemove.call(this, type, listener, opts);
    if (!listener) return;
    const m = targetMap.get(this);
    if (!m) return;
    const key = listenerKey(type, normalizeCapture(opts));
    const prev = m.get(key) ?? 0;
    if (prev <= 0) return;
    m.set(key, prev - 1);
    listenerCount--;
  };

  const origRAF = window.requestAnimationFrame.bind(window);
  const origCAF = window.cancelAnimationFrame.bind(window);
  const activeHandles = new Set<number>();

  window.requestAnimationFrame = (cb: FrameRequestCallback): number => {
    const id = origRAF((t) => {
      activeHandles.delete(id);
      rafPending = activeHandles.size;
      cb(t);
    });
    activeHandles.add(id);
    rafPending = activeHandles.size;
    return id;
  };
  window.cancelAnimationFrame = (id: number): void => {
    origCAF(id);
    activeHandles.delete(id);
    rafPending = activeHandles.size;
  };

  installed = true;
}

// Auto-install if the URL contains ?memprobe=1. Safe no-op on server / SSR.
if (runtimeProbeEnabled) installListenerProbe();
